package at.apa.statistictracker.data.local

import at.apa.statistictracker.data.model.userproperty.*
import at.apa.statistictracker.db.StatisticTrackerDb
import at.apa.statistictracker.db.UserPropertyTable
import at.apa.statistictracker.logger.Logger
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext

internal class UserPropertyDao(database: StatisticTrackerDb) {

    private val userPropertyQueries = database.userPropertyQueries

    /**
     * Loads all statistic payloads from the database.
     */
    suspend fun getAllUserProperties(): List<UserProperty> =
        withContext(Dispatchers.Default) {
            Logger.d(TAG, "getAllUserProperties()")

            userPropertyQueries
                .selectAll()
                .executeAsList()
                .map { it.toUserProperty() }
                .also {
                    Logger.d(TAG, "getAllUserProperties() fetched $it")
                }
        }

    /**
     * Inserts a [UserProperty] into the database.
     *
     * @param userProperty the user property to be saved to the database.
     */
    suspend fun insertOrReplace(userProperty: UserProperty) =
        withContext(Dispatchers.Default) {
            Logger.d(TAG, "insertOrUpdate(userProperty: $userProperty)")
            userPropertyQueries.insertOrReplace(userProperty.toUserPropertyTable())
        }

    private fun UserPropertyTable.toUserProperty(): UserProperty =
        when (UserPropertyType.valueOf(this.userPropertyTypeTag)) {
            UserPropertyType.MPS_UP_REGION -> MpsUserProperty.UPRegion(this.value_)
            UserPropertyType.MPS_UP_FREE_DAYS_ACTIVE -> MpsUserProperty.UPFreeDaysActive(this.value_.toBoolean())
            UserPropertyType.MPS_UP_LOGGED_IN -> MpsUserProperty.UPLoggedIn(this.value_.toBoolean())
        }

    private fun UserProperty.toUserPropertyTable(): UserPropertyTable = UserPropertyTable(
        this.getTypeTag(),
        value_ = when (this) {
            is MpsUserProperty.UPFreeDaysActive -> this.freeDaysActive.toString()
            is MpsUserProperty.UPLoggedIn -> this.loggedIn.toString()
            is MpsUserProperty.UPRegion -> this.region
        }
    )

    private enum class UserPropertyType {
        MPS_UP_REGION,
        MPS_UP_FREE_DAYS_ACTIVE,
        MPS_UP_LOGGED_IN
    }

    private fun UserProperty.getTypeTag(): String = when (this) {
        is MpsUserProperty.UPFreeDaysActive -> UserPropertyType.MPS_UP_FREE_DAYS_ACTIVE.name
        is MpsUserProperty.UPLoggedIn -> UserPropertyType.MPS_UP_LOGGED_IN.name
        is MpsUserProperty.UPRegion -> UserPropertyType.MPS_UP_REGION.name
    }

    companion object {
        private const val TAG = "UserPropertyDao"
    }

}

